import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Navigation from "../components/Navigation";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  MessageCircle,
  Send,
  ArrowLeft,
  Search,
  Loader2,
  Check,
  CheckCheck,
  MoreVertical,
  Trash2,
  Users,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";

export default function Messages() {
  const { recipientId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [friends, setFriends] = useState([]);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/messages/conversations`);
      setConversations(response.data.conversations);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch friends for starting new conversations
  const fetchFriends = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/friends`);
      setFriends(response.data.friends);
    } catch (error) {
      console.error("Error fetching friends:", error);
    }
  }, []);

  // Fetch messages for a conversation
  const fetchMessages = useCallback(async (userId) => {
    setLoadingMessages(true);
    try {
      const response = await axios.get(`${API}/messages/${userId}`);
      setMessages(response.data.messages);
      // Scroll to bottom
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("You can only message friends");
        navigate("/messages");
      } else {
        console.error("Error fetching messages:", error);
      }
    } finally {
      setLoadingMessages(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchConversations();
    fetchFriends();
  }, [fetchConversations, fetchFriends]);

  // Handle recipient from URL
  useEffect(() => {
    if (recipientId) {
      // Find in conversations or friends
      const conv = conversations.find(c => c.participant_id === recipientId);
      if (conv) {
        setSelectedConversation(conv);
      } else {
        const friend = friends.find(f => f.id === recipientId);
        if (friend) {
          setSelectedConversation({
            participant_id: friend.id,
            participant_name: friend.name,
            participant_photo: friend.profile_photo,
            participant_online: friend.is_online,
          });
        }
      }
      fetchMessages(recipientId);
    }
  }, [recipientId, conversations, friends, fetchMessages]);

  // Poll for new messages when in a conversation
  useEffect(() => {
    if (!recipientId) return;
    
    const interval = setInterval(() => {
      fetchMessages(recipientId);
    }, 5000); // Every 5 seconds

    return () => clearInterval(interval);
  }, [recipientId, fetchMessages]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !recipientId) return;

    setSending(true);
    try {
      const response = await axios.post(`${API}/messages/${recipientId}`, {
        content: newMessage.trim(),
      });
      setMessages((prev) => [...prev, response.data]);
      setNewMessage("");
      fetchConversations(); // Update conversation list
      
      // Scroll to bottom
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to send message";
      toast.error(message);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const deleteMessage = async (messageId) => {
    try {
      await axios.delete(`${API}/messages/${messageId}`);
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
      toast.success("Message deleted");
    } catch (error) {
      toast.error("Failed to delete message");
    }
  };

  const selectConversation = (conv) => {
    setSelectedConversation(conv);
    navigate(`/messages/${conv.participant_id}`);
  };

  const startNewConversation = (friend) => {
    setSelectedConversation({
      participant_id: friend.id,
      participant_name: friend.name,
      participant_photo: friend.profile_photo,
      participant_online: friend.is_online,
    });
    navigate(`/messages/${friend.id}`);
  };

  const getPhotoUrl = (photo) => {
    if (!photo) return null;
    if (photo.startsWith("http")) return photo;
    return `${process.env.REACT_APP_BACKEND_URL}${photo}`;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: "short" });
    } else {
      return date.toLocaleDateString([], { month: "short", day: "numeric" });
    }
  };

  // Filter friends not in existing conversations for "New Chat" section
  const existingParticipantIds = conversations.map(c => c.participant_id);
  const filteredFriends = friends.filter(f => 
    !existingParticipantIds.includes(f.id) &&
    (!searchQuery || f.name?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const filteredConversations = conversations.filter(c =>
    !searchQuery || c.participant_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50">
      <Navigation />

      <main className="max-w-6xl mx-auto px-4 py-6 pb-24">
        <div className="flex gap-4 h-[calc(100vh-200px)]">
          {/* Conversations List */}
          <div className={`${recipientId ? "hidden md:block" : ""} w-full md:w-80 flex-shrink-0`}>
            <Card className="h-full bg-white/90 backdrop-blur">
              <CardContent className="p-0 h-full flex flex-col">
                {/* Header */}
                <div className="p-4 border-b">
                  <h1 className="text-xl font-bold flex items-center gap-2 mb-3" data-testid="messages-title">
                    <MessageCircle className="w-6 h-6 text-amber-600" />
                    Messages
                  </h1>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search conversations..."
                      className="pl-9 h-9 rounded-full bg-gray-50"
                      data-testid="search-conversations"
                    />
                  </div>
                </div>

                {/* Conversations */}
                <ScrollArea className="flex-1">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                    </div>
                  ) : (
                    <div className="p-2">
                      {/* Existing Conversations */}
                      {filteredConversations.length > 0 && (
                        <div className="space-y-1">
                          {filteredConversations.map((conv) => (
                            <button
                              key={conv.id}
                              onClick={() => selectConversation(conv)}
                              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                                selectedConversation?.participant_id === conv.participant_id
                                  ? "bg-amber-100"
                                  : "hover:bg-gray-50"
                              }`}
                              data-testid={`conversation-${conv.participant_id}`}
                            >
                              <div className="relative">
                                <Avatar className="w-12 h-12">
                                  <AvatarImage src={getPhotoUrl(conv.participant_photo)} />
                                  <AvatarFallback className="bg-amber-500 text-white">
                                    {conv.participant_name?.charAt(0)}
                                  </AvatarFallback>
                                </Avatar>
                                {conv.participant_online && (
                                  <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                                )}
                              </div>
                              <div className="flex-1 text-left min-w-0">
                                <div className="flex items-center justify-between">
                                  <span className="font-medium truncate">{conv.participant_name}</span>
                                  <span className="text-xs text-gray-400">
                                    {formatTime(conv.last_message_time)}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <p className="text-sm text-gray-500 truncate">
                                    {conv.last_message || "No messages yet"}
                                  </p>
                                  {conv.unread_count > 0 && (
                                    <Badge className="bg-amber-500 text-white text-xs ml-2">
                                      {conv.unread_count}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Friends to start new chat */}
                      {filteredFriends.length > 0 && (
                        <div className="mt-4">
                          <p className="text-xs text-gray-400 px-3 mb-2">Start a new chat</p>
                          <div className="space-y-1">
                            {filteredFriends.map((friend) => (
                              <button
                                key={friend.id}
                                onClick={() => startNewConversation(friend)}
                                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-all"
                                data-testid={`new-chat-${friend.id}`}
                              >
                                <div className="relative">
                                  <Avatar className="w-10 h-10">
                                    <AvatarImage src={getPhotoUrl(friend.profile_photo)} />
                                    <AvatarFallback className="bg-gray-400 text-white">
                                      {friend.name?.charAt(0)}
                                    </AvatarFallback>
                                  </Avatar>
                                  {friend.is_online && (
                                    <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white" />
                                  )}
                                </div>
                                <span className="font-medium text-sm">{friend.name}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Empty state */}
                      {filteredConversations.length === 0 && filteredFriends.length === 0 && (
                        <div className="text-center py-12 px-4">
                          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                          <p className="text-gray-500 text-sm">
                            {friends.length === 0
                              ? "Add friends to start chatting!"
                              : "No conversations found"}
                          </p>
                          {friends.length === 0 && (
                            <Button
                              onClick={() => navigate("/friends")}
                              variant="outline"
                              className="mt-3"
                            >
                              Find Friends
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Chat Area */}
          <div className={`${!recipientId ? "hidden md:flex" : "flex"} flex-1 flex-col`}>
            {selectedConversation ? (
              <Card className="flex-1 bg-white/90 backdrop-blur flex flex-col">
                {/* Chat Header */}
                <div className="flex items-center gap-3 p-4 border-b">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="md:hidden"
                    onClick={() => {
                      setSelectedConversation(null);
                      navigate("/messages");
                    }}
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  <div
                    className="relative cursor-pointer"
                    onClick={() => navigate(`/profile/${selectedConversation.participant_id}`)}
                  >
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={getPhotoUrl(selectedConversation.participant_photo)} />
                      <AvatarFallback className="bg-amber-500 text-white">
                        {selectedConversation.participant_name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    {selectedConversation.participant_online && (
                      <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h2
                      className="font-semibold cursor-pointer hover:text-amber-600"
                      onClick={() => navigate(`/profile/${selectedConversation.participant_id}`)}
                    >
                      {selectedConversation.participant_name}
                    </h2>
                    <p className="text-xs text-gray-500">
                      {selectedConversation.participant_online ? (
                        <span className="text-green-600">Online</span>
                      ) : (
                        "Offline"
                      )}
                    </p>
                  </div>
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  {loadingMessages ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <MessageCircle className="w-16 h-16 text-gray-200 mb-4" />
                      <p className="text-gray-500">
                        No messages yet. Say hi to {selectedConversation.participant_name}!
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4" data-testid="messages-list">
                      {messages.map((msg) => {
                        const isOwn = msg.sender_id === user?.id;
                        return (
                          <div
                            key={msg.id}
                            className={`flex ${isOwn ? "justify-end" : "justify-start"}`}
                            data-testid={`message-${msg.id}`}
                          >
                            <div
                              className={`max-w-[70%] flex items-end gap-2 ${
                                isOwn ? "flex-row-reverse" : ""
                              }`}
                            >
                              {!isOwn && (
                                <Avatar className="w-8 h-8">
                                  <AvatarImage src={getPhotoUrl(msg.sender_photo)} />
                                  <AvatarFallback className="bg-gray-400 text-white text-xs">
                                    {msg.sender_name?.charAt(0)}
                                  </AvatarFallback>
                                </Avatar>
                              )}
                              <div
                                className={`rounded-2xl px-4 py-2 ${
                                  isOwn
                                    ? "bg-amber-500 text-white rounded-br-none"
                                    : "bg-gray-100 text-gray-900 rounded-bl-none"
                                }`}
                              >
                                <p className="text-sm">{msg.content}</p>
                                <div
                                  className={`flex items-center gap-1 mt-1 text-xs ${
                                    isOwn ? "text-amber-100 justify-end" : "text-gray-400"
                                  }`}
                                >
                                  <span>{formatTime(msg.created_at)}</span>
                                  {isOwn && (
                                    msg.read ? (
                                      <CheckCheck className="w-3 h-3" />
                                    ) : (
                                      <Check className="w-3 h-3" />
                                    )
                                  )}
                                </div>
                              </div>
                              {isOwn && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6 opacity-0 group-hover:opacity-100"
                                    >
                                      <MoreVertical className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent>
                                    <DropdownMenuItem
                                      className="text-red-600"
                                      onClick={() => deleteMessage(msg.id)}
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </ScrollArea>

                {/* Message Input */}
                <div className="p-4 border-t">
                  <div className="flex gap-2">
                    <Input
                      ref={inputRef}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                      placeholder="Type a message..."
                      className="rounded-full"
                      data-testid="message-input"
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={!newMessage.trim() || sending}
                      className="rounded-full bg-amber-500 hover:bg-amber-600"
                      data-testid="send-message-btn"
                    >
                      {sending ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </Button>
                  </div>
                </div>
              </Card>
            ) : (
              <Card className="flex-1 bg-white/90 backdrop-blur flex items-center justify-center">
                <div className="text-center">
                  <MessageCircle className="w-20 h-20 text-gray-200 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-gray-700 mb-2">
                    Your Messages
                  </h2>
                  <p className="text-gray-500">
                    Select a conversation or start a new chat
                  </p>
                </div>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
